"use client"

import { useState, useEffect, Suspense, useCallback } from "react"
import { useSearchParams } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Search, Building2, X, Loader2 } from "lucide-react"
import { Property } from "@/lib/supabase"
import PropertyCardHorizontal from "@/components/property-card-horizontal"

function PropertiesContent() {
  const searchParams = useSearchParams()
  const [properties, setProperties] = useState<Property[]>([])
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)

  // Initialize filter states from URL parameters
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || "")
  const [propertyType, setPropertyType] = useState(() => searchParams.get('property_type') || "")
  const [listingType, setListingType] = useState(() => searchParams.get('listing_type') || "")
  const [city, setCity] = useState(() => searchParams.get('city') || "")
  const [neighborhood, setNeighborhood] = useState(() => searchParams.get('neighborhood') || "")
  const [minPrice, setMinPrice] = useState("any")
  const [maxPrice, setMaxPrice] = useState("any")
  const [selectedBedrooms, setSelectedBedrooms] = useState<string[]>([])
  const [sortBy, setSortBy] = useState("created_at-desc")
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 12

  // Update filters when URL parameters change
  useEffect(() => {
    const urlListingType = searchParams.get('listing_type')
    const urlPropertyType = searchParams.get('property_type')
    const urlSearch = searchParams.get('search')
    const urlCity = searchParams.get('city')
    const urlNeighborhood = searchParams.get('neighborhood')
    const urlBedrooms = searchParams.get('bedrooms')

    if (urlListingType && urlListingType !== listingType) {
      setListingType(urlListingType)
    }

    if (urlPropertyType && urlPropertyType !== propertyType) {
      setPropertyType(urlPropertyType)
    }

    if (urlSearch && urlSearch !== searchQuery) {
      setSearchQuery(urlSearch)
    }

    if (urlCity && urlCity !== city) {
      setCity(urlCity)
    }

    if (urlNeighborhood && urlNeighborhood !== neighborhood) {
      setNeighborhood(urlNeighborhood)
    }

    if (urlBedrooms && !selectedBedrooms.includes(urlBedrooms)) {
      setSelectedBedrooms([urlBedrooms])
    }
  }, [searchParams])

  // Fetch properties when filters change
  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true)

      const params = new URLSearchParams({
        limit: itemsPerPage.toString(),
        page: currentPage.toString(),
      })

      const [sortField, sortOrder] = sortBy.split('-')
      params.append('sort_by', sortField)
      params.append('sort_order', sortOrder)

      if (listingType) {
        params.append('listing_type', listingType)
      }

      if (propertyType) {
        params.append('property_type', propertyType)
      }

      if (searchQuery) {
        params.append('search', searchQuery)
      }

      if (city) {
        params.append('city', city)
      }

      if (neighborhood) {
        params.append('neighborhood', neighborhood)
      }

      if (minPrice && minPrice !== 'any') {
        params.append('min_price', minPrice)
      }

      if (maxPrice && maxPrice !== 'any') {
        params.append('max_price', maxPrice)
      }

      if (selectedBedrooms.length > 0) {
        params.append('bedrooms', selectedBedrooms.join(','))
      }

      const response = await fetch(`/api/properties?${params.toString()}`)

      if (!response.ok) {
        throw new Error('Failed to fetch properties')
      }

      const data = await response.json()
      const responseData = data.data || data
      const fetchedProperties = responseData.properties || []
      const total = responseData.total || responseData.pagination?.total || fetchedProperties.length

      setProperties(fetchedProperties)
      setTotalCount(total)
    } catch (error) {
      console.error('Error fetching properties:', error)
      setProperties([])
      setTotalCount(0)
    } finally {
      setLoading(false)
    }
  }, [
    currentPage,
    sortBy,
    listingType,
    propertyType,
    searchQuery,
    city,
    minPrice,
    maxPrice,
    selectedBedrooms.join(','),
  ])

  useEffect(() => {
    fetchProperties()
  }, [fetchProperties])

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentPage])

  const resetFilters = () => {
    setSearchQuery("")
    setPropertyType("")
    setListingType("")
    setCity("")
    setMinPrice("any")
    setMaxPrice("any")
    setSelectedBedrooms([])
    setSortBy("created_at-desc")
    setCurrentPage(1)
  }

  const hasActiveFilters = searchQuery || propertyType || listingType || city || (minPrice !== 'any') || (maxPrice !== 'any') || selectedBedrooms.length > 0

  const totalPages = Math.ceil(totalCount / itemsPerPage)

  // Bedroom options
  const bedroomOptions = [
    { value: 'studio', label: 'Studio' },
    { value: '1', label: '1 BR' },
    { value: '2', label: '2 BR' },
    { value: '3', label: '3 BR' },
    { value: '4', label: '4 BR' },
    { value: '5', label: '5 BR' },
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-20">
        {/* Filters Section */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
            <div className="space-y-6">
              {/* Search Bar */}
              <div>
                <h3 className="font-bold mb-3 text-gray-900 text-sm">Search by Location or Building</h3>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="e.g., BGC, Makati, Forbes Park, One Rockwell..."
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#d60500] focus:outline-none text-sm"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Listing Type */}
              <div>
                <h3 className="font-bold mb-3 text-gray-900 text-sm">Listing Type</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setListingType("")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      listingType === ""
                        ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setListingType("For Sale")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      listingType === "For Sale"
                        ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'
                    }`}
                  >
                    For Sale
                  </button>
                  <button
                    onClick={() => setListingType("For Rent")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      listingType === "For Rent"
                        ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'
                    }`}
                  >
                    For Rent
                  </button>
                </div>
              </div>

              {/* Property Type */}
              <div>
                <h3 className="font-bold mb-3 text-gray-900 text-sm">Property Type</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setPropertyType("")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      propertyType === ""
                        ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'
                    }`}
                  >
                    All Types
                  </button>
                  <button
                    onClick={() => setPropertyType("condo")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      propertyType === "condo"
                        ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'
                    }`}
                  >
                    Condominium
                  </button>
                  <button
                    onClick={() => setPropertyType("house_and_lot")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      propertyType === "house_and_lot"
                        ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'
                    }`}
                  >
                    House & Lot
                  </button>
                  <button
                    onClick={() => setPropertyType("penthouse")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      propertyType === "penthouse"
                        ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'
                    }`}
                  >
                    Penthouse
                  </button>
                  <button
                    onClick={() => setPropertyType("villa")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      propertyType === "villa"
                        ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'
                    }`}
                  >
                    Villa
                  </button>
                  <button
                    onClick={() => setPropertyType("townhouse")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                      propertyType === "townhouse"
                        ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'
                    }`}
                  >
                    Townhouse
                  </button>
                </div>
              </div>

              {/* Bedrooms */}
              <div>
                <h3 className="font-bold mb-3 text-gray-900 text-sm">Bedrooms</h3>
                <div className="flex flex-wrap gap-2">
                  {bedroomOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSelectedBedrooms(prev =>
                          prev.includes(option.value)
                            ? prev.filter(b => b !== option.value)
                            : [...prev, option.value]
                        )
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                        selectedBedrooms.includes(option.value)
                          ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Min Price */}
                <div>
                  <h3 className="font-bold mb-3 text-gray-900 text-sm">Min Price</h3>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => setMinPrice("any")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${minPrice === "any" ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'}`}>Any</button>
                    <button onClick={() => setMinPrice("5000000")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${minPrice === "5000000" ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'}`}>₱5M</button>
                    <button onClick={() => setMinPrice("10000000")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${minPrice === "10000000" ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'}`}>₱10M</button>
                    <button onClick={() => setMinPrice("20000000")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${minPrice === "20000000" ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'}`}>₱20M</button>
                    <button onClick={() => setMinPrice("50000000")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${minPrice === "50000000" ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'}`}>₱50M</button>
                    <button onClick={() => setMinPrice("100000000")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${minPrice === "100000000" ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'}`}>₱100M</button>
                  </div>
                </div>

                {/* Max Price */}
                <div>
                  <h3 className="font-bold mb-3 text-gray-900 text-sm">Max Price</h3>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => setMaxPrice("any")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${maxPrice === "any" ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'}`}>Any</button>
                    <button onClick={() => setMaxPrice("10000000")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${maxPrice === "10000000" ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'}`}>₱10M</button>
                    <button onClick={() => setMaxPrice("20000000")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${maxPrice === "20000000" ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'}`}>₱20M</button>
                    <button onClick={() => setMaxPrice("50000000")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${maxPrice === "50000000" ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'}`}>₱50M</button>
                    <button onClick={() => setMaxPrice("100000000")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${maxPrice === "100000000" ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'}`}>₱100M</button>
                    <button onClick={() => setMaxPrice("200000000")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${maxPrice === "200000000" ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'}`}>₱200M</button>
                    <button onClick={() => setMaxPrice("500000000")} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${maxPrice === "500000000" ? 'bg-[#d60500] text-white shadow-md border-2 border-[#d60500]' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-2 border-gray-200'}`}>₱500M</button>
                  </div>
                </div>
              </div>

              {/* Filter Actions */}
              {hasActiveFilters && (
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" className="border-gray-300 hover:bg-gray-50" onClick={resetFilters}>
                    <X className="w-4 h-4 mr-2" />
                    Reset Filters
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sort Bar */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 bg-white rounded-xl p-4 shadow-sm">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{totalCount} {totalCount === 1 ? 'Property' : 'Properties'} Found</h2>
            {totalPages > 1 && <p className="text-sm text-gray-600">Page {currentPage} of {totalPages}</p>}
          </div>
          <div className="flex gap-2">
            <Button variant={sortBy === "created_at-desc" ? "default" : "outline"} size="sm" onClick={() => setSortBy("created_at-desc")} className={`text-sm ${sortBy === "created_at-desc" ? 'bg-[#d60500] hover:bg-[#b50400]' : ''}`}>Newest</Button>
            <Button variant={sortBy === "price-asc" ? "default" : "outline"} size="sm" onClick={() => setSortBy("price-asc")} className={`text-sm ${sortBy === "price-asc" ? 'bg-[#d60500] hover:bg-[#b50400]' : ''}`}>Price: Low</Button>
            <Button variant={sortBy === "price-desc" ? "default" : "outline"} size="sm" onClick={() => setSortBy("price-desc")} className={`text-sm ${sortBy === "price-desc" ? 'bg-[#d60500] hover:bg-[#b50400]' : ''}`}>Price: High</Button>
          </div>
        </div>

        {/* Properties List */}
        {loading ? (
          <div className="space-y-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-64 md:h-80 bg-white rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl shadow-sm">
            <div className="max-w-md mx-auto px-4">
              <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">No properties found</h3>
              <p className="text-gray-600 mb-6">{hasActiveFilters ? 'Try adjusting your filters to see more results.' : 'No properties available at this time.'}</p>
              {hasActiveFilters && (
                <Button onClick={resetFilters} className="bg-[#d60500] hover:bg-[#b50400]">Clear All Filters</Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {properties.map((property) => (
              <PropertyCardHorizontal key={property.id} property={property} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="min-w-[100px]">Previous</Button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum
              if (totalPages <= 5) {
                pageNum = i + 1
              } else if (currentPage <= 3) {
                pageNum = i + 1
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i
              } else {
                pageNum = currentPage - 2 + i
              }
              return (
                <Button key={i} variant={currentPage === pageNum ? "default" : "outline"} size="sm" onClick={() => setCurrentPage(pageNum)} className={`min-w-[40px] ${currentPage === pageNum ? 'bg-[#d60500] hover:bg-[#b50400]' : ''}`}>{pageNum}</Button>
              )
            })}
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="min-w-[100px]">Next</Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default function PropertiesPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-[#d60500] border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600 font-medium">Loading properties...</p>
        </div>
      </div>
    }>
      <PropertiesContent />
    </Suspense>
  )
}
